import { useState } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import TaskCard from './TaskCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus } from 'lucide-react'

interface TaskLabel {
  id: string
  name: string
  color: string
}

interface TaskItem {
  id: string
  title: string
  description: string | null
  position: number
  priority: 'no_priority' | 'low' | 'medium' | 'high' | 'urgent'
  assignee: { id: string; name: string } | null
  labels: TaskLabel[]
  updated_at: string
}

interface Column {
  id: string
  name: string
  color: string | null
  position: number
  tasks: TaskItem[]
}

interface Props {
  column: Column
  onCreateTask: (columnId: string, title: string) => void
  onTaskClick: (task: TaskItem) => void
  onDeleteTask: (task: TaskItem) => void
}

export default function KanbanColumn({ column, onCreateTask, onTaskClick, onDeleteTask }: Props) {
  const [showAdd, setShowAdd] = useState(false)
  const [newTitle, setNewTitle] = useState('')

  const { setNodeRef, isOver } = useDroppable({ id: column.id })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (newTitle.trim()) {
      onCreateTask(column.id, newTitle.trim())
      setNewTitle('')
      setShowAdd(false)
    }
  }

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 flex flex-col rounded-lg bg-gray-50 border transition-colors ${
        isOver ? 'border-gray-400' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          {column.color && (
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: column.color }} />
          )}
          <h3 className="text-sm font-semibold">{column.name}</h3>
          <span className="text-xs text-gray-400">{column.tasks.length}</span>
        </div>
      </div>

      <div className="flex-1 p-2 space-y-2 min-h-[60px]">
        <SortableContext items={column.tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {column.tasks.map(task => (
            <TaskCard key={task.id} task={task} onClick={() => onTaskClick(task)} onDelete={() => onDeleteTask(task)} />
          ))}
        </SortableContext>
      </div>

      <div className="p-2 border-t border-gray-200">
        {showAdd ? (
          <form onSubmit={handleSubmit} className="space-y-2">
            <Input
              placeholder="Task title..."
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              autoFocus
              className="text-sm"
            />
            <div className="flex gap-1">
              <Button type="submit" size="sm" className="text-xs">Add</Button>
              <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => { setShowAdd(false); setNewTitle('') }}>Cancel</Button>
            </div>
          </form>
        ) : (
          <Button variant="ghost" size="sm" className="w-full text-xs text-gray-500" onClick={() => setShowAdd(true)}>
            <Plus className="mr-1 h-3 w-3" /> Add task
          </Button>
        )}
      </div>
    </div>
  )
}
